import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Palette, Check } from 'lucide-react';

interface Theme {
  id: string;
  name: string;
  description: string;
  className: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

const themes: Theme[] = [
  {
    id: 'default',
    name: 'Optimization Orange & Steel',
    description: 'High-visibility orange and steel blue for maximum efficiency focus',
    className: '',
    colors: {
      primary: '#e97316',
      secondary: '#0ea5e9',
      accent: '#fef3e2'
    }
  },
  {
    id: 'earth',
    name: 'Earth & Stone',
    description: 'Natural earth tones with sage greens for sustainable construction',
    className: 'theme-earth',
    colors: {
      primary: '#059669',
      secondary: '#d97706',
      accent: '#ecfdf5'
    }
  },
  {
    id: 'industrial',
    name: 'Industrial Blue',
    description: 'Cool professional blues with steel grays for enterprise efficiency',
    className: 'theme-industrial',
    colors: {
      primary: '#1e40af',
      secondary: '#0891b2',
      accent: '#dbeafe'
    }
  },
  {
    id: 'safety',
    name: 'Safety High-Vis',
    description: 'Maximum visibility colors for safety-focused optimization',
    className: 'theme-safety',
    colors: {
      primary: '#f59e0b',
      secondary: '#ef4444',
      accent: '#fef3c7'
    }
  },
  {
    id: 'slate',
    name: 'Corporate Slate',
    description: 'Sophisticated slate tones for executive optimization dashboards',
    className: 'theme-slate',
    colors: {
      primary: '#475569',
      secondary: '#0ea5e9',
      accent: '#f1f5f9'
    }
  }
];

export function ThemeSelector() {
  const [currentTheme, setCurrentTheme] = useState('default');
  const [isOpen, setIsOpen] = useState(false);

  const applyTheme = (theme: Theme) => {
    // Remove existing theme classes
    themes.forEach(t => {
      if (t.className) {
        document.documentElement.classList.remove(t.className);
      }
    });

    // Apply new theme class
    if (theme.className) {
      document.documentElement.classList.add(theme.className);
    }

    setCurrentTheme(theme.id);
    setIsOpen(false);
    
    // Store theme preference
    localStorage.setItem('optibuild-theme', theme.id);
  };

  // Initialize theme from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('optibuild-theme');
    if (savedTheme) {
      const theme = themes.find(t => t.id === savedTheme);
      if (theme) {
        applyTheme(theme);
      }
    }
  }, []);

  const getCurrentTheme = () => themes.find(t => t.id === currentTheme) || themes[0];

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Palette className="h-4 w-4 mr-2" />
          Theme
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] modal-content">
        <DialogHeader>
          <DialogTitle>Choose Your Optimization Theme</DialogTitle>
          <DialogDescription>
            Select a color theme that maximizes your focus and efficiency
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="text-sm text-muted-foreground mb-2">
            Current theme: <strong>{getCurrentTheme().name}</strong>
          </div>
          
          <div className="grid gap-3">
            {themes.map((theme) => (
              <Card 
                key={theme.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  currentTheme === theme.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => applyTheme(theme)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300" 
                          style={{ backgroundColor: theme.colors.primary }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300" 
                          style={{ backgroundColor: theme.colors.secondary }}
                        />
                        <div 
                          className="w-4 h-4 rounded-full border border-gray-300" 
                          style={{ backgroundColor: theme.colors.accent }}
                        />
                      </div>
                      <div>
                        <h4 className="font-medium">{theme.name}</h4>
                        <p className="text-sm text-muted-foreground">{theme.description}</p>
                      </div>
                    </div>
                    {currentTheme === theme.id && (
                      <Badge variant="default" className="flex items-center gap-1">
                        <Check className="h-3 w-3" />
                        Active
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <h4 className="font-medium text-sm mb-2">Theme Optimization Tips:</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• <strong>Optimization Orange & Steel:</strong> Maximum visibility for efficiency-focused work</li>
              <li>• <strong>Earth & Stone:</strong> Ideal for sustainable and green construction projects</li>
              <li>• <strong>Industrial Blue:</strong> Professional efficiency for corporate environments</li>
              <li>• <strong>Safety High-Vis:</strong> Optimized for safety-critical project management</li>
              <li>• <strong>Corporate Slate:</strong> Sophisticated optimization for executive dashboards</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}